import React, { useState } from "react";
import {
  Button,
  DatePicker,
  DatePickerInput,
  Loading,
  Select,
  SelectItem,
} from "carbon-components-react";
import { Rocket32 } from "@carbon/icons-react";
import { connect } from "react-redux";

import { AppShape, DocShape, DocTypes } from "@eave/shared/types/api";

import DocDisplay from "underwriter_dashboard/shared/DocDisplay";
import HurdleView from "underwriter_dashboard/shared/HurdleView";
import { HurdleProps } from "underwriter_dashboard/HurdleEditor";
import {
  docTypeToHumanStr,
  getLatestCreatedDocOfType,
  getLatestCreatedDocOfTypeIncludingUnusedDocs,
  getSortedDocsOfType,
} from "underwriter_dashboard/shared";
import { loadApp } from "underwriter_dashboard/actions";

import {
  errorDetailToAlertText,
  isoToPickerDate,
  pickerToIsoDate,
  todayAsIsoDate,
} from "./helpers";
import { generatePostClosingPdf, markDocsUnused } from "./api";

const styles = require("./styles.module.scss");

interface PostClosingDoc {
  docType: DocTypes;
  needsTargetDate: boolean;
  getLatestCreatedDocOfTypeFunc(app: AppShape, docType: string): DocShape | null;
}

const orderedPostClosingDocs: PostClosingDoc[] = [
  {
    docType: DocTypes.Allonge, // special case where doc is marked unused on create
    needsTargetDate: false,
    getLatestCreatedDocOfTypeFunc: getLatestCreatedDocOfTypeIncludingUnusedDocs,
  },
  {
    docType: DocTypes.GoodbyeLetter,
    needsTargetDate: false,
    getLatestCreatedDocOfTypeFunc: getLatestCreatedDocOfType,
  },
  {
    docType: DocTypes.HOITransferOfServicing,
    needsTargetDate: false,
    getLatestCreatedDocOfTypeFunc: getLatestCreatedDocOfType,
  },
  {
    docType: DocTypes.MortgageStatement,
    needsTargetDate: true,
    getLatestCreatedDocOfTypeFunc: getLatestCreatedDocOfType,
  },
  {
    docType: DocTypes.PaymentHistoryStatement,
    needsTargetDate: false,
    getLatestCreatedDocOfTypeFunc: getLatestCreatedDocOfType,
  },
  {
    docType: DocTypes.PostClosingPayoffStatement,
    needsTargetDate: true,
    getLatestCreatedDocOfTypeFunc: getLatestCreatedDocOfType,
  },
];

const docSelectOptions = orderedPostClosingDocs.map(d => ({
  value: d.docType,
  label: docTypeToHumanStr(d.docType),
}));

const docTypeNeedsTargetDate = (docType: DocTypes): boolean =>
  orderedPostClosingDocs.find(d => d.docType === docType).needsTargetDate;

interface Props extends HurdleProps {
  dispatch: any;
}

const PostClosingDocs: React.FC<Props> = ({ app, hurdle, onHide, dispatch }) => {
  const initialDocType = orderedPostClosingDocs[0].docType;

  const [busy, setBusy] = useState<boolean>(false);
  const [selectedDocType, setSelectedDocType] = useState<DocTypes>(initialDocType);
  const [targetDate, setTargetDate] = useState<string | null>(
    docTypeNeedsTargetDate(initialDocType) ? todayAsIsoDate : null,
  );

  const generateDocAndMarkExistingDocsOfSameTypeUnused = (docType: DocTypes) => {
    setBusy(true);

    const existingDocs = getSortedDocsOfType(app.documents, docType);

    generatePostClosingPdf(app.id, docType, targetDate)
      .then(() => {
        loadApp(dispatch, app.id);

        if (existingDocs && existingDocs.length) {
          markDocsUnused(existingDocs);
        }
      })
      .catch(({ meta: { error_detail } }) => {
        alert(errorDetailToAlertText(error_detail));
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const needsTargetDate = docTypeNeedsTargetDate(selectedDocType);

  const docsToShow = orderedPostClosingDocs
    .map(d => d.getLatestCreatedDocOfTypeFunc(app, d.docType))
    .filter(d => d !== null); // filter out any non-existent docs

  return (
    <HurdleView
      app={app}
      hurdle={hurdle}
      onHide={onHide}
      className={styles.hurdle}
      contentClassName={styles.content}
      hideTitle
    >
      {busy && <Loading />}
      <h1 className={styles.title}>Post-closing docs</h1>
      <div className={styles.controls}>
        <div className={styles.input}>
          <Select
            id="doc-type"
            labelText="Select doc to generate"
            value={selectedDocType}
            onChange={e => {
              const newDocType = e.target.value;
              setSelectedDocType(newDocType);
              setTargetDate(docTypeNeedsTargetDate(newDocType) ? todayAsIsoDate : null);
            }}
          >
            {docSelectOptions.map(opt => (
              <SelectItem key={opt.value} text={opt.label} value={opt.value} />
            ))}
          </Select>
        </div>
        <div className={styles.input}>
          <DatePicker
            datePickerType="single"
            value={targetDate ? isoToPickerDate(targetDate) : ""}
            onChange={e => setTargetDate(e.length > 0 ? pickerToIsoDate(e[0]) : todayAsIsoDate)}
          >
            <DatePickerInput
              id="target-date"
              labelText={`Target date${needsTargetDate ? "" : " –⁠ does not apply to this doc"}`}
              placeholder="mm/dd/yyyy"
              disabled={busy || !needsTargetDate}
            />
          </DatePicker>
        </div>
        <div className={styles.button}>
          <Button
            renderIcon={Rocket32}
            disabled={busy}
            onClick={() => generateDocAndMarkExistingDocsOfSameTypeUnused(selectedDocType)}
          >
            Generate
          </Button>
        </div>
      </div>
      {docsToShow.length ? (
        <div className={styles.docs}>
          <DocDisplay docs={docsToShow} />
        </div>
      ) : (
        <p className={styles.note}>
          Use the controls above to generate docs, which will then appear here
        </p>
      )}
    </HurdleView>
  );
};

export default connect()(PostClosingDocs);
